/* eslint-disable react-refresh/only-export-components */
/* eslint-disable no-mixed-spaces-and-tabs */

import React, { createContext, useCallback, useContext, useEffect, useMemo, useState, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import {
	getV1FilterResource,
	getV1FormBuilder,
	getV1FormBuilderSchemaFieldsName,
	getV1PagesResource,
	patchV1PagesPageId
} from '../api-client/api.system-feature/api';
import { IResources, routeToColumnsMap, routeToResourceMap } from '../constants/route-page-map'; // Removed - should be passed as props or configured by consuming app
import { getToken } from '../utils/common';
import { removeIdStaticIdsFromMongoObject } from '../utils/common';
import { RuleGroupType } from 'react-querybuilder';
import { MaterialTableColumnProps } from '../components/material-table/material-table';
import _ from 'lodash';
import { formatLabel } from '../utils/format-text';
import PageSkeleton from '../components/loaders/page-skeleton';
import { enqueueSnackbar } from 'notistack';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../hooks/useAuth';
// import { usePermissions } from '../hooks/use-permissions';

/* Interfaces and types */
export type ViewType = 'Table' | 'Grid' | 'Kanban' | 'Calendar' | 'Gantt' | '';

interface SchemaFields {
	select: string;
	alias: string;
	type: string;
	filter_type?: string;
	filter_name?: string;
	required?: boolean;
	isOrderAllowed?: boolean;
	isDefault?: boolean;
	isFilterAllowed?: boolean;
	label?: string;
	key: string;
	accessorKey?: string;
	joinKey?: string;
	enum?: string | string[];
}
export interface SavedViewType {
	label: ViewType;
	position: number;
}
export interface PageState {
	_id: string | number;
	name: string;
	page_size: number;
	column_order: string[];
	visible_columns: MaterialTableColumnProps[];
	views: SavedViewType[];
	active_view: ViewType;
	filters: RuleGroupType;
	filterQueryString: string;
	sort: Record<any, any> | null;
	search: string;
	enabled_views: ViewType[];
	isShared: boolean;
	column_sizes: Record<string, number>;
}

export interface GeneratedColumnsState {
	columns: MaterialTableColumnProps[];
	orderKeys: string[];
}

export const initialFilter: RuleGroupType = { combinator: 'and', rules: [] };

interface PageContextValue {
	pageTemplateId: string;
	activePage: PageState;
	updatePageInfo: (updates: Partial<PageState>) => void;
	loading: boolean;
	isSaving: boolean;
	error: string | null;
	pages: any[];
	savePages: () => void;
	setActivePage: (data: any) => void;
	setPages: React.Dispatch<any>;
	schemaFields: SchemaFields[];
	initializeColumns: (columns: MaterialTableColumnProps[]) => void;
	generatedColumns: GeneratedColumnsState;
	sharedPages: any[];
	updateGeneratedColumns: (updates: Partial<GeneratedColumnsState>) => void;
	formData: Record<string, any>
	setFormData: (data: Record<string, any>) => void
	setIsCreatePage: (isCreatePage: boolean) => void
	customRouteToResourceMap?: Record<string, any>;
}

export const defaultPageState: PageState = {
	_id: '',
	name: '',
	page_size: 20,
	column_order: ['id'],
	visible_columns: [],
	views: [],
	active_view: '',
	filters: initialFilter,
	filterQueryString: '',
	sort: null,
	search: '',
	enabled_views: ['Table', 'Grid', 'Kanban', 'Calendar', 'Gantt'],
	isShared: false,
	column_sizes: {}
};
interface PageProviderProps {
	children: React.ReactNode;
	routeToColumnsMap?: Record<string, any>;
	routeToResourceMap?: Record<string, any>;
}

const UpdatePageInfo = () => { };
const UpdateGeneratedColumns = () => { };

export const PageContext = createContext<PageContextValue>({
	pageTemplateId: '',
	activePage: defaultPageState,
	updatePageInfo: UpdatePageInfo,
	loading: false,
	isSaving: false,
	error: null,
	pages: [],
	sharedPages: [],
	savePages: () => { },
	setActivePage: () => { },
	setPages: () => { },
	schemaFields: [],
	initializeColumns: () => { },
	generatedColumns: { columns: [], orderKeys: [] },
	updateGeneratedColumns: UpdateGeneratedColumns,
	formData: {},
	setFormData: () => { },
	setIsCreatePage: () => { },
});

function mergeArraysKeepingOrder(array1: string[], array2: string[]): string[] {
	// Create a Set from the first array for efficient lookups
	const array1Set = new Set(array1);

	// Filter the second array to find elements not in the first array
	const elementsToAdd = array2.filter((item) => !array1Set.has(item));

	// Combine the original first array with the new elements
	return [...array1, ...elementsToAdd];
}

const generateColumnSchema = (
	defaultColumns: MaterialTableColumnProps[],
	schemaFields: SchemaFields[],
	columnOrders: string[],
	dColumns?: any[],
	t?: any
): { columns: MaterialTableColumnProps[]; orderKeys: string[] } => {
	try {
		// if (dColumns && dColumns?.length) {
		// 	defaultColumns = defaultColumns.map((column) => {
		// 		const f = dColumns.find((c) => c.accessorKey == column.accessorKey);
		// 		return f || column
		// 	})
		// }
		if (dColumns && dColumns?.length) {
			defaultColumns = defaultColumns.map((column) => {
				const f = dColumns.find((c) => c.accessorKey == column.accessorKey);
				const merged = f ? { ...f, ...column, type: f.type ?? column.type } : column;
				return merged
			})
		}
		let orderKeys = columnOrders.length
			? columnOrders
			: (defaultColumns.map((column) => column.accessorKey) as string[]);
		const getAccesorKeyViaField = (field: SchemaFields) =>
			(field.type === 'object' || field?.join) ? `${field.alias}.${field.accessorKey}` : field.key;

		// Clean up schema fields by removing "__replaceKey" from the keys
		const updatedFields = schemaFields.map((field) => ({
			...field,
			key: field.key.replace('__replaceKey', '')
		})).filter((field) => !field.is_inner_join);

		const uniqueFields = _.differenceWith(
			updatedFields,
			updatedFields,
			(fieldA, fieldB) => fieldA.key === fieldB?.joinKey
		);

		// Find fields in schemaFields but not in defaultColumns based on accessorKey
		const unmatchedFields = _.differenceWith(
			uniqueFields,
			defaultColumns,
			(field, column) => getAccesorKeyViaField(field) === column.accessorKey
		);

		// Map unique fields to MaterialTableColumn structure
		const additionalColumns: MaterialTableColumnProps[] = unmatchedFields.map(
			(field) => ({
				header: t ? formatLabel(t(field?.label)?.replace('_data', '') || '') : formatLabel(field?.label?.replace('_data', '') || ''),
				accessorKey: getAccesorKeyViaField(field),
				visible: false,
				type: field?.enum ? 'status' : field.type,
				...(field.type === 'array' && { typeArrayAccessorKey: field.accessorKey || '' }),
				enableSorting: (field.type === 'array' || field.type === 'object' || field?.join) ? false : true,
				...(field?.type === 'boolean' ? {
					typeBooleanLabels: field?.values?.length ? _.mapValues(_.keyBy(
						field?.values.map(item => ({
							key: item.name,
							value: item.label
						})),
						'key'
					), 'value') : { 0: "Inactive", 1: "Active", false: "Inactive", true: "Active" }
				} : {})
			})
		);

		const updatedDefaultColumns = defaultColumns?.map((column) => {
			const field = schemaFields.find((field) => getAccesorKeyViaField(field) === column.accessorKey);
			return {
				...column,
				...((field?.type === 'object' || field?.joinKey || column.enableSorting === false) ? { enableSorting: false } : {})
			};
		});

		// Combine default columns and additional columns
		const columns = [...updatedDefaultColumns, ...additionalColumns];

		// Generate an array of column keys for ordering
		const nOrderKeys = columns.map((column) => column.accessorKey) as string[];

		orderKeys = mergeArraysKeepingOrder(orderKeys, nOrderKeys);

		return { columns, orderKeys };
	} catch (error) {
		return {
			columns: defaultColumns,
			orderKeys: defaultColumns.map((column) => column.accessorKey) as string[]
		};
	}
};

export const PageProvider: React.FC<PageProviderProps> = ({ children, routeToColumnsMap: customRouteToColumnsMap, routeToResourceMap: customRouteToResourceMap }) => {
	const location = useLocation();
	const [activePage, setActivePageData] = useState<PageState>(defaultPageState);
	// const permissions=usePermissions();
	const [schemaFields, setSchemaFields] = useState<SchemaFields[]>([]);
	const [loading, setLoading] = useState<boolean>(true);
	const [isSaving, setIsSaving] = useState<boolean>(false);
	const [error, setError] = useState<string | null>(null);
	const [pages, setPages] = useState<any>([]);
	const [sharedPages, setSharedPages] = useState<any>([]);
	const [isChanged, setIsChanged] = useState<boolean>(false);
	const [pageTemplateId, setPageTemplateId] = useState<string>('');
	const [isInitialLoad, setIsInitialLoad] = useState<boolean>(true);
	const [isCreatePage, setIsCreatePage] = useState<boolean>(false);
	const [formData, setFormData] = useState<any>({});
	const [generatedColumns, setGeneratedColumns] = useState<{
		columns: MaterialTableColumnProps[];
		orderKeys: string[];
	}>({ columns: [], orderKeys: [] });
	const { user } = useAuth()
	// Add ref to track current pathname for race condition prevention
	const currentPathnameRef = useRef<string>(location.pathname);
	const curPathName = useMemo(() => location.pathname, [location.pathname])

	// Get current page's default columns and resources
	const defaultColumns: any[] = useMemo(() => {
		const columns = (customRouteToColumnsMap?.[curPathName] || routeToColumnsMap[curPathName])?.defaultColumns || [];
		return columns;
	}, [curPathName, customRouteToColumnsMap]);

	const resources: IResources = useMemo(() => {
		const resource = customRouteToResourceMap?.[curPathName] || routeToResourceMap[curPathName] || null;
		return resource;
	}, [curPathName, customRouteToResourceMap]);

	//Translation
	const { t } = useTranslation()

	// eslint-disable-next-line unused-imports/no-unused-vars, @typescript-eslint/no-unused-vars
	const initializeColumns = (columns: MaterialTableColumnProps[]) => {
		//! Remove the function if not needed
	};

	// Fetch page-related information from API
	const fetchPageData = useCallback(
		async (currentPath: string, currentDefaultColumns: any[], currentResources: IResources) => {

			// Check if this request is still valid (pathname hasn't changed)
			if (currentPathnameRef.current !== currentPath) {
				return;
			}

			setLoading(true);
			setError(null);

			try {
				if (currentDefaultColumns.length && currentResources) {
					const { pageResource = '', fieldResource = '', formResource = '' } = currentResources;

					// Double-check pathname hasn't changed before making API calls
					if (currentPathnameRef.current !== currentPath) {
						return;
					}

					const [pageResponse, fieldResponse, formResponse] = await Promise.all([
						pageResource ? getV1PagesResource({ ...getToken(), resource: pageResource }) : Promise.resolve(null),

						getV1FormBuilderSchemaFieldsName({
							name: fieldResource,
							...getToken(),
							skip: 0,
							limit: 999
						}),
						getV1FormBuilder({
							...getToken(),
							filters: `(resource.eq=${formResource}&company_id.eq=${user?.company_id})`,
						})
					]);

					// Final check - ensure pathname still matches
					if (currentPathnameRef.current !== currentPath) {
						return;
					}

					const fields = fieldResponse?.data?.fields?.map(f => ({ ...f, label: !f?.label?.trim() ? formatLabel(f.key) : f.label }));

					const gColumns = generateColumnSchema(currentDefaultColumns, fields, [], [], t);

					const pages =
						pageResponse?.data?.pages?.map((p) => ({
							...defaultPageState,
							...p,
							visible_columns: generateColumnSchema(
								p.visible_columns?.length ? p.visible_columns : currentDefaultColumns,
								fields,
								p.column_order || [],
								currentDefaultColumns,
								t
							).columns,
							column_order: mergeArraysKeepingOrder(
								p.column_order || [],
								_.uniq(gColumns.orderKeys)
							)
						})) || [];
					const sharedPages =
						pageResponse?.data?.shared_pages?.map((sPages) => ({
							...sPages.details,
							isShared: true,
							owner: sPages.owner,
							_id: sPages.id
						})) || [];

					const uPages = [...pages, ...sharedPages];

					// Only update state if we're still on the same path
					if (currentPathnameRef.current === currentPath) {
						await Promise.all([
							setSchemaFields(fields),
							setGeneratedColumns(gColumns),
							setPageTemplateId(pageResponse?.data?._id),
							setSharedPages(sharedPages),
							setPages(uPages || []),
							setFormData(formResponse?.data?.form?.[0] || {}),
							setActivePageData((prev) => ({
								...prev,
								...(pages?.[0] || {}) // Merge API response with current state
							}))
						]);
					}
				}
			} catch (err) {
				// Only set error if we're still on the same path
				if (currentPathnameRef.current === currentPath) {
					setError('Failed to load page data');
				}
			} finally {
				// Only update loading state if we're still on the same path
				if (currentPathnameRef.current === currentPath) {
					setTimeout(() => {
						setLoading(false);
					}, 1000);
				}
			}
		},
		//eslint-disable-next-line react-hooks/exhaustive-deps
		[] // Remove dependencies to prevent stale closures
	);

	// Update page state dynamically
	const updatePageInfo = (updates: Partial<PageState>) => {
		setActivePageData((prev) => ({
			...prev,
			...updates
		}));
		!isInitialLoad && setIsChanged(true)
		setIsInitialLoad(false)
	};

	// Update columns state dynamically
	const updateGeneratedColumns = (updates: Partial<GeneratedColumnsState>) => {
		setGeneratedColumns((prev) => ({
			...prev,
			...updates
		}));
	};

	const setActivePage = (page: PageState) => {
		const uPages = pages.map((p) =>
			p._id == activePage?._id ? activePage : p
		);
		setPages(uPages);
		setActivePageData(page);
	};

	const savePages = async () => {
		setError(null);
		setIsSaving(true);
		await addOrDeletePage()
		setTimeout(() => {
			setIsSaving(false);
		}, 1000);
	};
	const fetchSavedFilters = async (): Promise<void> => {

		await getV1FilterResource({ ...getToken(), resource: resources?.pageResource || resources?.fieldResource })

	};

	const addOrDeletePage = async (restPages?: any[]) => {
		try {
			const filteredPages = restPages?.length ? restPages : pages.filter((page) => !page.isShared);
			const uPages = filteredPages.map((p) =>
				(p._id == activePage?._id && activePage.name == p.name) ? activePage : p
			);
			const sanitizedPages = removeIdStaticIdsFromMongoObject(uPages);

			const response = await patchV1PagesPageId({
				...getToken(),
				...{ page_id: pageTemplateId, pages: sanitizedPages }
			});
			if (response.status_code === 200) {
				const updatedPages = response?.data?.pages || [];
				setPages(updatedPages);
				fetchSavedFilters();
				setActivePageData((prev) => ({
					...prev,
					...(updatedPages.find((p) => p.name == prev.name) || {})
				}));
				setIsChanged(false);
				setIsCreatePage(false);
				setFormData(response?.data?.form?.[0] || {});
				const msg = restPages?.length ? t('pages.msg.delete') : t('pages.msg.save')
				enqueueSnackbar(msg);
			}
			return response
		} catch (err) {
			setError('Failed to save page data');
		}
	}

	const deletePage = async (pages: string) => {
		addOrDeletePage(pages);
	}

	// Handle pathname changes
	useEffect(() => {
		// Update the ref immediately
		currentPathnameRef.current = curPathName;

		// Reset state for new page
		setLoading(true);
		setIsSaving(false);
		setActivePageData(defaultPageState);
		setIsChanged(false);
		setIsInitialLoad(true);

		// Clear previous data to prevent showing stale data
		setSchemaFields([]);
		setGeneratedColumns({ columns: [], orderKeys: [] });
		setPages([]);
		setSharedPages([]);
		setPageTemplateId('');
		setError(null);

		// Fetch data for new page with current values
		fetchPageData(curPathName, defaultColumns, resources);

		return () => { setLoading(true) }
		//eslint-disable-next-line react-hooks/exhaustive-deps
	}, [curPathName, JSON.stringify(defaultColumns), JSON.stringify(resources)]); // Remove other dependencies to prevent issues


	if ((loading || curPathName != currentPathnameRef.current) && resources) {
		return <PageSkeleton columnCount={6} rowCount={defaultPageState.page_size} />;
	}

	return (
		<PageContext.Provider
			value={{
				pageTemplateId,
				activePage,
				pages,
				sharedPages,
				schemaFields,
				setPages,
				updatePageInfo,
				savePages,
				setActivePage,
				isSaving,
				loading,
				error,
				initializeColumns,
				generatedColumns,
				updateGeneratedColumns,
				isChanged,
				deletePage,
				isCreatePage,
				setIsCreatePage,
				addOrDeletePage,
				setIsChanged,
				formData,
				customRouteToResourceMap
			}}>
			{children}
		</PageContext.Provider>
	);
};

// Custom hook to use the PageContext
export const usePageContext = () => {
	const context = useContext(PageContext);
	if (!context) {
		throw new Error('usePageContext must be used within a PageProvider');
	}
	return context;
};
